import React, { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { motion } from "framer-motion";
import axios from "axios";

type MonthPoint = { month: string; count: number };

/* eslint-disable camelcase */
type Order = { created_at: string };

function groupByMonth(orders: Order[]): MonthPoint[] {
  const counts: Record<string, number> = {};
  orders.forEach((order) => {
    if (!order.created_at) return;
    const d = new Date(order.created_at);
    const key = d.toLocaleString("default", {
      month: "short",
      year: "2-digit",
    });
    counts[key] = (counts[key] ?? 0) + 1;
  });
  return Object.entries(counts).map(([month, total]) => ({
    month,
    count: total,
  }));
}
/* eslint-enable camelcase */

type Props = { jwtToken: string };

export default function OrdersOverviewChart({ jwtToken }: Props) {
  const [data, setData] = useState<MonthPoint[]>([]);

  useEffect(() => {
    if (!jwtToken) return;
    axios
      .get("/api/v1/orders", {
        headers: { Authorization: `Bearer ${jwtToken}` },
      })
      .then((res) => setData(groupByMonth(res.data.data ?? [])))
      .catch(() => {});
  }, [jwtToken]);

  return (
    <motion.div
      className="bg-gray-800 bg-opacity-50 backdrop-blur-md shadow-lg rounded-xl p-6 border border-gray-700"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
    >
      <h2 className="text-lg font-medium mb-4 text-gray-100">
        Orders Over Time
      </h2>
      {data.length === 0 ? (
        <div className="h-80 flex items-center justify-center text-gray-500 text-sm">
          No order data yet
        </div>
      ) : (
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#4B5563" />
              <XAxis dataKey="month" stroke="#9CA3AF" />
              <YAxis stroke="#9CA3AF" allowDecimals={false} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "rgba(31,41,55,0.8)",
                  borderColor: "#4B5563",
                }}
                itemStyle={{ color: "#E5E7EB" }}
              />
              <Line
                type="monotone"
                dataKey="count"
                stroke="#6366F1"
                strokeWidth={3}
                dot={{ fill: "#6366F1", strokeWidth: 2, r: 6 }}
                activeDot={{ r: 8, strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </motion.div>
  );
}
